/**
 * SharePanel - the customer share-link panel (step 2, privileged roles only):
 * create / list / per-row Copy / Revoke.
 *
 * Create / revoke read-only, no-login customer progress links. Same
 * fixed-position / outside-click / Escape / bottom-sheet pattern as CellPopover
 * and ZoneScopePanel. The tokenized URL is returned ONLY by the creating POST —
 * the GET list never contains it - so it's shown once, with a Copy button and
 * a save-it-now warning. Any 403 closes the panel quietly (the privileged
 * check in DisciplinePicker should prevent it ever being seen).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getJson, sendJson, SVM_SHARE_URL, svmShareListUrl } from './svmApi';
import {
  BTN_GHOST,
  BTN_PRIMARY,
  GREEN,
  INPUT,
  LABEL,
  POPOVER_BOX,
  SHEET_BACKDROP,
  SHEET_BOX,
  SHEET_HANDLE,
  fullStamp,
  useDismiss,
  useIsNarrow,
} from './svmModel';

export default function SharePanel({ rect, project, onClose }) {
  const narrow = useIsNarrow(); // phone → bottom sheet, like CellPopover
  const [state, setState] = useState({ loading: true, error: null, links: [] });
  const [created, setCreated] = useState(null); // { id, url, createdAt } - this open only
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const ref = useRef(null);
  const urlRef = useRef(null);
  const copyTimer = useRef(null);

  useDismiss(ref, onClose);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    []
  );

  const loadLinks = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    getJson(svmShareListUrl(project))
      .then((d) => setState({ loading: false, error: null, links: d.links || [] }))
      .catch((err) => {
        if (err.status === 403) {
          onClose();
          return;
        }
        setState({ loading: false, error: err.message || 'Failed to load links', links: [] });
      });
  }, [project, onClose]);
  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const createLink = async () => {
    setCreating(true);
    setCreateErr(null);
    try {
      const d = await sendJson('POST', SVM_SHARE_URL, { project });
      setCreated(d.link);
      setCopied(false);
      loadLinks();
    } catch (err) {
      if (err.status === 403) {
        onClose();
        return;
      }
      setCreateErr(err.message || "Couldn't create the link");
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = async () => {
    if (!created || !created.url) return;
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the URL so the user can copy manually.
      if (urlRef.current) {
        urlRef.current.focus();
        urlRef.current.select();
      }
    }
  };

  // Re-copy an ACTIVE link straight from the list (tokens are stored, so a
  // link you sent last week can be copied again until it's revoked).
  const copyRow = async (l) => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(l.url);
      setCopiedId(l.id);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiedId(null), 2000);
    } catch {
      window.prompt('Copy the link:', l.url);
    }
  };

  const revoke = async (id) => {
    setRevokingId(id);
    try {
      await sendJson('DELETE', SVM_SHARE_URL, { id });
      loadLinks();
    } catch (err) {
      if (err.status === 403) {
        onClose();
        return;
      }
      setState((s) => ({ ...s, error: err.message || "Couldn't revoke the link" }));
    } finally {
      setRevokingId(null);
    }
  };

  const W = 340;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const left = Math.max(8, Math.min(rect.left - W + 90, vw - W - 12));
  const below = rect.bottom + 8;
  const top = below + 340 > vh ? Math.max(8, rect.top - 348) : below;

  const boxStyle = narrow ? SHEET_BOX : { ...POPOVER_BOX, left, top, width: W };

  return (
    <>
      {narrow && <div style={SHEET_BACKDROP} onClick={onClose} />}
      <div ref={ref} style={boxStyle}>
        {narrow && <div style={SHEET_HANDLE} />}
        <div style={{ fontSize: 11.5, fontWeight: 800, color: '#0D0D0D', marginBottom: 4 }}>Customer view link</div>
        <div style={{ fontSize: 10.5, color: '#999', lineHeight: 1.4, marginBottom: 10 }}>
          Anyone with this link can WATCH this project's SVM progress - read-only, no login, notes hidden. Active links can be re-copied
          below; revoke anytime.
        </div>

        {createErr && <div style={{ fontSize: 11, color: '#B71C1C', fontWeight: 600, marginBottom: 8 }}>{createErr}</div>}

        {created ? (
          <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#8D6E00', marginBottom: 6 }}>
              Link created - copy and send it (you can re-copy it from the list below anytime).
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                ref={urlRef}
                readOnly
                value={created.url}
                onFocus={(e) => e.target.select()}
                style={{ ...INPUT, fontSize: 11, padding: '6px 8px', flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                style={{ ...BTN_PRIMARY, fontSize: 11, padding: narrow ? '10px 14px' : '6px 12px', whiteSpace: 'nowrap' }}
                onClick={copyUrl}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              style={{ ...BTN_PRIMARY, fontSize: 11, padding: narrow ? '10px 16px' : '6px 12px', opacity: creating ? 0.6 : 1 }}
              disabled={creating}
              onClick={createLink}
            >
              {creating ? 'Creating…' : 'Create link'}
            </button>
          </div>
        )}

        <div style={{ ...LABEL, marginBottom: 6 }}>Links for this project</div>
        {state.loading && <div style={{ fontSize: 11, color: '#999', padding: '4px 0' }}>Loading…</div>}
        {state.error && <div style={{ fontSize: 11, color: '#B71C1C', fontWeight: 600, padding: '4px 0' }}>{state.error}</div>}
        {!state.loading && !state.error && state.links.length === 0 && (
          <div style={{ fontSize: 11, color: '#999', padding: '4px 0' }}>No links yet.</div>
        )}
        {!state.loading &&
          state.links.map((l) => (
            <div
              key={l.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #F2F2F2', padding: narrow ? '9px 0' : '7px 0' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: l.revokedAt ? '#AAA' : '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fullStamp(l.createdAt)}
                  {l.createdBy ? ` · ${l.createdBy}` : ''}
                </div>
                <div style={{ fontSize: 10.5, color: '#999', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {l.views || 0} {(l.views || 0) === 1 ? 'view' : 'views'} · last viewed{' '}
                  {l.lastViewedAt ? fullStamp(l.lastViewedAt) : 'never'}
                </div>
              </div>
              {l.revokedAt ? (
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 800,
                    color: '#999',
                    background: '#EDEDED',
                    borderRadius: 20,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  revoked
                </span>
              ) : (
                <>
                  {l.url && (
                    <button
                      type="button"
                      title="Copy this link again"
                      style={{
                        ...BTN_GHOST,
                        fontSize: 11,
                        padding: narrow ? '10px 14px' : '6px 10px',
                        color: GREEN,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => copyRow(l)}
                    >
                      {copiedId === l.id ? 'Copied' : 'Copy'}
                    </button>
                  )}
                  <button
                    type="button"
                    style={{
                      ...BTN_GHOST,
                      fontSize: 11,
                      padding: narrow ? '10px 14px' : '6px 10px',
                      color: '#B71C1C',
                      whiteSpace: 'nowrap',
                      opacity: revokingId === l.id ? 0.6 : 1,
                    }}
                    disabled={revokingId === l.id}
                    onClick={() => revoke(l.id)}
                  >
                    {revokingId === l.id ? 'Revoking…' : 'Revoke'}
                  </button>
                </>
              )}
            </div>
          ))}
      </div>
    </>
  );
}
